import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const VendorDeletionModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  vendor,
  relationshipData = null,
  isLoading = false,
  onArchive,
  onCascadeDelete
}) => {
  const [selectedOption, setSelectedOption] = useState('archive');
  const [confirmationText, setConfirmationText] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setConfirmationText('');
      setShowDetails(false);
      // Set default option based on relationship data
      if (relationshipData?.recommended_action === 'delete') {
        setSelectedOption('delete');
      } else {
        setSelectedOption('archive');
      }
    }
  }, [isOpen, relationshipData]);

  if (!isOpen || !vendor) return null;

  const handleConfirm = () => {
    switch (selectedOption) {
      case 'archive':
        onArchive?.(vendor?.id);
        break;
      case 'delete':
        onConfirm?.(vendor?.id);
        break;
      case 'cascade':
        if (confirmationText?.toLowerCase() === 'delete all') {
          onCascadeDelete?.(vendor?.id);
        }
        break;
      default:
        break;
    }
  };

  const handleClose = () => {
    if (!isLoading && !isAnalyzing) {
      setConfirmationText('');
      setShowDetails(false);
      onClose?.();
    }
  };

  const getTotalRelatedRecords = () => {
    if (!relationshipData?.related_records) return 0;
    const records = relationshipData?.related_records;
    return (records?.bills || 0) + (records?.payments || 0) + (records?.expenses || 0) + 
           (records?.vendor_credits || 0) + (records?.recurring_expenses || 0);
  };

  const getDeletionRisk = () => {
    return relationshipData?.deletion_risk || 'unknown';
  };

  const getRecommendedAction = () => {
    return relationshipData?.recommended_action || 'archive';
  };

  const isConfirmationValid = () => {
    switch (selectedOption) {
      case 'cascade':
        return confirmationText?.toLowerCase() === 'delete all';
      case 'delete': case'archive':
        return true;
      default:
        return false;
    }
  };

  const totalRelated = getTotalRelatedRecords();
  const risk = getDeletionRisk();
  const recommended = getRecommendedAction();

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-2xl">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center space-x-3 mb-6">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              risk === 'high' ? 'bg-error/10' : risk === 'medium' ? 'bg-warning/10' : 'bg-info/10'
            }`}>
              <Icon 
                name={risk === 'high' ? "AlertTriangle" : "Building"} 
                size={24} 
                className={
                  risk === 'high' ? 'text-error' : 
                  risk === 'medium' ? 'text-warning' : 'text-info'
                } 
              />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-foreground">Delete Vendor</h3>
              <p className="text-muted-foreground">
                {vendor?.vendor_name} ({vendor?.vendor_number})
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              disabled={isLoading || isAnalyzing}
              className="h-8 w-8"
            >
              <Icon name="X" size={16} />
            </Button>
          </div>

          {/* Relationship Summary */}
          {relationshipData && (
            <div className="mb-6 p-4 bg-muted/30 rounded-lg">
              <h4 className="text-sm font-medium text-foreground mb-3">Related Records Found</h4>
              <div className="grid grid-cols-2 gap-4">
                {relationshipData?.related_records?.bills > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Bills:</span>
                    <span className="text-sm font-medium">{relationshipData?.related_records?.bills}</span>
                  </div>
                )}
                {relationshipData?.related_records?.payments > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Payments:</span>
                    <span className="text-sm font-medium">{relationshipData?.related_records?.payments}</span>
                  </div>
                )}
                {relationshipData?.related_records?.expenses > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Expenses:</span>
                    <span className="text-sm font-medium">{relationshipData?.related_records?.expenses}</span>
                  </div>
                )}
                {relationshipData?.related_records?.vendor_credits > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Credits:</span>
                    <span className="text-sm font-medium">{relationshipData?.related_records?.vendor_credits}</span>
                  </div>
                )}
                {relationshipData?.related_records?.recurring_expenses > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Recurring:</span>
                    <span className="text-sm font-medium">{relationshipData?.related_records?.recurring_expenses}</span>
                  </div>
                )}
              </div>
              {totalRelated > 0 && (
                <div className="mt-3 pt-3 border-t border-border">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">Total Related Records:</span>
                    <span className="text-sm font-bold">{totalRelated}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Deletion Options */}
          <div className="space-y-4 mb-6">
            <h4 className="text-sm font-medium text-foreground">Choose an action:</h4>
            
            {/* Archive Option (Recommended) */}
            <label className={`flex items-start space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
              selectedOption === 'archive' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }`}>
              <input
                type="radio"
                name="deletionOption"
                value="archive"
                checked={selectedOption === 'archive'}
                onChange={(e) => setSelectedOption(e?.target?.value)}
                className="mt-1"
                disabled={isLoading || isAnalyzing}
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <Icon name="Archive" size={16} className="text-primary" />
                  <span className="font-medium text-foreground">Archive Vendor</span>
                  {recommended === 'archive' && (
                    <span className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded">
                      Recommended
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Mark vendor as archived while preserving all related records and history. 
                  This is the safest option and can be reversed.
                </p>
              </div>
            </label>

            {/* Delete Option (Clean) */}
            <label className={`flex items-start space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
              selectedOption === 'delete' 
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            } ${totalRelated > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <input
                type="radio"
                name="deletionOption"
                value="delete"
                checked={selectedOption === 'delete'}
                onChange={(e) => setSelectedOption(e?.target?.value)}
                disabled={totalRelated > 0 || isLoading || isAnalyzing}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <Icon name="Trash2" size={16} className="text-muted-foreground" />
                  <span className="font-medium text-foreground">Delete Vendor Only</span>
                  {totalRelated === 0 && recommended === 'delete' && (
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                      Safe
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {totalRelated > 0 
                    ? `Cannot delete - vendor has ${totalRelated} related records`
                    : 'Permanently remove vendor with no related records. This is safe and clean.'
                  }
                </p>
              </div>
            </label>

            {/* Cascade Delete Option (Dangerous) */}
            <label className={`flex items-start space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
              selectedOption === 'cascade' ?'border-error bg-error/5' :'border-border hover:border-error/50'
            } ${totalRelated > 10 ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <input
                type="radio"
                name="deletionOption"
                value="cascade"
                checked={selectedOption === 'cascade'}
                onChange={(e) => setSelectedOption(e?.target?.value)}
                disabled={totalRelated > 10 || isLoading || isAnalyzing}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertTriangle" size={16} className="text-error" />
                  <span className="font-medium text-foreground">Delete All Related Data</span>
                  <span className="text-xs bg-error text-error-foreground px-2 py-1 rounded">
                    Dangerous
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {totalRelated > 10
                    ? `Too many related records (${totalRelated}). Use archive option instead.`
                    : `Permanently delete vendor and ALL related records (${totalRelated} items). This cannot be undone.`
                  }
                </p>
              </div>
            </label>
          </div>

          {/* Confirmation Input for Dangerous Actions */}
          {selectedOption === 'cascade' && totalRelated <= 10 && (
            <div className="space-y-2 mb-6 p-4 bg-error/5 border border-error/20 rounded-lg">
              <label className="block text-sm font-medium text-error">
                This will permanently delete {totalRelated} related records. Type "DELETE ALL" to confirm:
              </label>
              <input
                type="text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e?.target?.value)}
                placeholder="DELETE ALL"
                className="w-full px-3 py-2 border border-error focus:ring-2 focus:ring-error focus:border-error rounded-lg"
                disabled={isLoading || isAnalyzing}
              />
            </div>
          )}

          {/* Impact Details */}
          {showDetails && relationshipData && (
            <div className="mb-6 p-4 bg-muted/20 border border-border rounded-lg">
              <h4 className="text-sm font-medium text-foreground mb-3">Detailed Impact Analysis</h4>
              <div className="space-y-3">
                {selectedOption === 'archive' && (
                  <div className="space-y-2">
                    <h5 className="text-sm font-medium text-green-700">Archive Impact:</h5>
                    <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                      <li>• Vendor will be hidden from active lists</li>
                      <li>• All related records remain intact</li>
                      <li>• Historical data and reports unaffected</li>
                      <li>• Action can be reversed by reactivating</li>
                      <li>• No data loss whatsoever</li>
                    </ul>
                  </div>
                )}
                
                {selectedOption === 'delete' && totalRelated === 0 && (
                  <div className="space-y-2">
                    <h5 className="text-sm font-medium text-green-700">Safe Deletion:</h5>
                    <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                      <li>• No related records to affect</li>
                      <li>• Clean removal from database</li>
                      <li>• No impact on other data</li>
                      <li>• Cannot be reversed</li>
                    </ul>
                  </div>
                )}

                {selectedOption === 'cascade' && totalRelated > 0 && (
                  <div className="space-y-2">
                    <h5 className="text-sm font-medium text-error">Cascade Deletion Impact:</h5>
                    <ul className="text-sm text-error/80 space-y-1 ml-4">
                      {relationshipData?.related_records?.bills > 0 && (
                        <li>• {relationshipData?.related_records?.bills} bills will have vendor reference removed</li>
                      )}
                      {relationshipData?.related_records?.payments > 0 && (
                        <li>• {relationshipData?.related_records?.payments} payments will be permanently deleted</li>
                      )}
                      {relationshipData?.related_records?.expenses > 0 && (
                        <li>• {relationshipData?.related_records?.expenses} expenses will be permanently deleted</li>
                      )}
                      {relationshipData?.related_records?.vendor_credits > 0 && (
                        <li>• {relationshipData?.related_records?.vendor_credits} vendor credits will be permanently deleted</li>
                      )}
                      {relationshipData?.related_records?.recurring_expenses > 0 && (
                        <li>• {relationshipData?.related_records?.recurring_expenses} recurring expenses will be permanently deleted</li>
                      )}
                      <li className="font-medium text-error">• This action cannot be reversed</li>
                      <li className="font-medium text-error">• Historical reports may be affected</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Toggle Details */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-sm text-primary hover:underline flex items-center space-x-1 mb-6"
            type="button"
            disabled={isLoading || isAnalyzing}
          >
            <span>{showDetails ? 'Hide' : 'Show'} impact details</span>
            <Icon 
              name={showDetails ? 'ChevronUp' : 'ChevronDown'} 
              size={14} 
            />
          </button>

          {/* Risk Assessment */}
          {risk !== 'low' && (
            <div className={`mb-6 p-4 rounded-lg border ${
              risk === 'high' ?'bg-error/10 border-error/20' :'bg-warning/10 border-warning/20'
            }`}>
              <div className="flex items-start space-x-2">
                <Icon 
                  name="AlertTriangle" 
                  size={16} 
                  className={risk === 'high' ? 'text-error' : 'text-warning'} 
                />
                <div>
                  <p className={`text-sm font-medium ${
                    risk === 'high' ? 'text-error' : 'text-warning'
                  }`}>
                    {risk === 'high' ? 'High Risk Deletion' : 'Medium Risk Deletion'}
                  </p>
                  <p className={`text-sm mt-1 ${
                    risk === 'high' ? 'text-error/80' : 'text-warning/80'
                  }`}>
                    {risk === 'high' ?'This vendor has active unpaid bills or unused credits. Deletion may affect financial records.' :'This vendor has historical records. Consider archiving to preserve data integrity.'
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div className="text-sm text-muted-foreground">
              {totalRelated > 0 && `${totalRelated} related records • `}
              Recommended: {recommended === 'archive' ? 'Archive' : 'Delete'}
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isLoading || isAnalyzing}
              >
                Cancel
              </Button>
              <Button
                variant={selectedOption === 'cascade' ? 'destructive' : selectedOption === 'archive' ? 'default' : 'destructive'}
                onClick={handleConfirm}
                disabled={
                  isLoading || 
                  isAnalyzing || 
                  !isConfirmationValid() ||
                  (selectedOption === 'delete' && totalRelated > 0) ||
                  (selectedOption === 'cascade' && totalRelated > 10)
                }
                loading={isLoading || isAnalyzing}
                iconName={
                  isLoading || isAnalyzing ? "Loader2" : 
                  selectedOption === 'archive' ? "Archive" : 
                  selectedOption === 'cascade' ? "AlertTriangle" : "Trash2"
                }
                iconPosition="left"
              >
                {isLoading || isAnalyzing ? 'Processing...' : 
                 selectedOption === 'archive' ? 'Archive Vendor' :
                 selectedOption === 'cascade' ? 'Delete All Data' : 'Delete Vendor'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorDeletionModal;